import { Avatar, Flex, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'

import type { UserVeryShort } from 'src/api/main'
import { computeInitials, pickAvatarColor } from 'src/components'
import { composeDisplayName } from 'src/utils/string'

const { Text } = Typography

const MAX_AVATARS = 5
const AVATAR_SIZE = 26

type Props = {
  members: UserVeryShort[]
}

export const GroupMembersCell = ({ members }: Props) => {
  const { t } = useTranslation()

  if (members.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    )
  }

  return (
    <Flex align="center" gap={8}>
      <Avatar.Group
        size={AVATAR_SIZE}
        max={{
          count: MAX_AVATARS,
          style: {
            background: 'var(--ant-color-primary)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600
          }
        }}
      >
        {members.map((member) => {
          const name = composeDisplayName(
            member.first_name,
            member.last_name,
            member.email
          )

          return (
            <Tooltip
              key={member.id}
              title={
                <Flex vertical>
                  <span style={{ fontWeight: 600 }}>{name}</span>
                  <span style={{ fontSize: 11, opacity: 0.75 }}>
                    {member.email}
                  </span>
                </Flex>
              }
            >
              <Avatar
                size={AVATAR_SIZE}
                src={member.photo?.file ?? undefined}
                style={{
                  background: pickAvatarColor(name || member.email),
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600
                }}
              >
                {computeInitials(
                  member.first_name,
                  member.last_name,
                  member.email
                )}
              </Avatar>
            </Tooltip>
          )
        })}
      </Avatar.Group>
      <Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
        {t('users.groups.memberCount', { count: members.length })}
      </Text>
    </Flex>
  )
}
